import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Pagination,
} from "@mui/material";
import {
    CloudUpload as CloudUploadIcon,
    Description as DescriptionIcon,
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    FolderOutlined as FolderIcon,
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import insightsApi from "../../api/insightsApi";
import useProjects from "../../hooks/useProjects";
import ProjectPicker from "../../components/Project/ProjectPicker";
import { canEditProjectContent, roleRefusalMessage } from "../../utils/projectRoles";

const ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "pptx", "xls", "xlsx"];
const isAllowedFile = (filename) => {
    const ext = (filename || "").split(".").pop()?.toLowerCase();
    return ALLOWED_EXTENSIONS.includes(ext);
};

// Extract the filename from a Content-Disposition response header.
// Handles both RFC 5987 `filename*=UTF-8''...` and the plain `filename="..."`
// forms. Returns null when nothing usable is present.
const filenameFromContentDisposition = (header) => {
    if (!header) return null;

    const utfMatch = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
    if (utfMatch && utfMatch[1]) {
        try {
            return decodeURIComponent(utfMatch[1].trim().replace(/^["']|["']$/g, ""));
        } catch {
            // fall through to the plain filename below
        }
    }

    const plainMatch = /filename="?([^";]+)"?/i.exec(header);
    if (plainMatch && plainMatch[1]) {
        return plainMatch[1].trim();
    }

    return null;
};

const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

// Standalone screen when rendered with no props, and the project workspace's
// Documents tab when given a `projectId`. Scoped mode filters the list to that
// project, uploads straight into it, and drops the page chrome the tab already
// provides — the title, the outer page padding, and the project picker that
// only exists because the standalone screen has no project of its own.
//
// `canEdit` (scoped mode) is false for a project VIEWER: they list, read and
// download the documents but cannot upload or delete. Unscoped, the same rule
// comes from each project's `my_role` on the cards useProjects loads.
export default function DocumentsPage({ projectId = null, canEdit = true }) {
    const scoped = projectId != null && projectId !== "";
    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const fileInputRef = useRef(null);

    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const DOCUMENTS_PAGE_SIZE = 10;
    const [documentsPage, setDocumentsPage] = useState(1);
    const [documentsTotalCount, setDocumentsTotalCount] = useState(0);
    const [documentsTotalPages, setDocumentsTotalPages] = useState(1);

    // Summary modal state
    const [summaryModalOpen, setSummaryModalOpen] = useState(false);
    const [summaryDoc, setSummaryDoc] = useState(null);

    // Delete confirmation state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Per-document download state (id currently downloading)
    const [downloadingId, setDownloadingId] = useState(null);

    // A document belongs to a project, and its scope is set once — at upload.
    // This page isn't project-scoped by its route, so the project to upload into
    // is chosen here, and uploading is blocked until one is selected.
    const [selectedProjectId, setSelectedProjectId] = useState("");

    // Fetch documents (with optional page, default 1)
    const fetchDocuments = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            const data = await insightsApi.getDocuments(null, {
                page,
                page_size: DOCUMENTS_PAGE_SIZE,
                project_id: scoped ? projectId : undefined,
            });
            const results = data.results || [];
            const count = data.count ?? 0;

            // The backend doesn't always honor the requested page_size, so derive the
            // effective size from the response. A page with a `next` is full, so its
            // length is the real page size.
            const effectivePageSize = data.next && results.length > 0
                ? results.length
                : DOCUMENTS_PAGE_SIZE;

            setDocuments(results);
            setDocumentsTotalCount(count);
            setDocumentsPage(page);
            setDocumentsTotalPages(Math.max(1, Math.ceil(count / effectivePageSize)));
        } catch (error) {
            console.error("Failed to load documents:", error);
            enqueueSnackbar(
                intl.formatMessage({ id: "documents.fetch_failed", defaultMessage: "Failed to load documents" }),
                { variant: "error" }
            );
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar, intl, scoped, projectId]);

    useEffect(() => {
        fetchDocuments(1);
    }, [fetchDocuments]);

    // The upload target selector's list (shared with the assessment setup
    // screens — see hooks/useProjects).
    const { projects, loading: projectsLoading } = useProjects({
        enabled: !scoped,
        onError: () =>
            enqueueSnackbar(
                intl.formatMessage({
                    id: "documents.projects_fetch_failed",
                    defaultMessage: "Failed to load projects",
                }),
                { variant: "error" }
            ),
    });

    // Download a document through the authenticated backend endpoint. The raw
    // GCS urls are private, so we fetch the bytes as a blob (carrying the auth
    // header) and trigger a client-side download from the resulting object url.
    const handleDownloadDocument = async (doc) => {
        if (downloadingId) return; // guard against double-clicks while in flight
        let blobUrl;
        try {
            setDownloadingId(doc.id);
            const res = await insightsApi.downloadDocument(doc.id);

            const filename =
                filenameFromContentDisposition(res.headers?.["content-disposition"]) ||
                doc.name ||
                `document-${doc.id}`;

            blobUrl = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (error) {
            const status = error?.response?.status;
            const messageId = status === 404 ? "documents.download_unavailable" : "documents.download_failed";
            const defaultMessage =
                status === 404
                    ? "File unavailable — it may have been removed."
                    : "Failed to download document";
            enqueueSnackbar(intl.formatMessage({ id: messageId, defaultMessage }), { variant: "error" });
        } finally {
            if (blobUrl) URL.revokeObjectURL(blobUrl);
            setDownloadingId(null);
        }
    };

    // Upload a single file into the selected project
    const uploadFile = async (file) => {
        try {
            setUploading(true);
            await insightsApi.uploadDocument(file, uploadProjectId);
            enqueueSnackbar(
                intl.formatMessage({ id: "documents.upload_success", defaultMessage: "Document uploaded successfully" }),
                { variant: "success" }
            );
            fetchDocuments(1);
        } catch (error) {
            console.error("Upload failed:", error);
            enqueueSnackbar(
                roleRefusalMessage(error, intl) ||
                intl.formatMessage({ id: "documents.upload_failed", defaultMessage: "Failed to upload document" }),
                { variant: "error" }
            );
        } finally {
            setUploading(false);
        }
    };

    const handleFileSelect = (files) => {
        if (!files || files.length === 0) return;
        // A document must belong to a project, so there's nowhere to put the file
        // until one is picked. Say so rather than dropping it silently. (Never
        // reachable when scoped — the project is already known.)
        if (!uploadProjectId) {
            enqueueSnackbar(
                intl.formatMessage({
                    id: "documents.select_project_first",
                    defaultMessage: "Select a project before uploading",
                }),
                { variant: "warning" }
            );
            return;
        }
        const file = files[0];
        if (!isAllowedFile(file.name)) {
            enqueueSnackbar(
                intl.formatMessage(
                    { id: "documents.unsupported_format", defaultMessage: "Unsupported format. Allowed: PDF, DOC, DOCX, PPTX, XLS, XLSX" }
                ),
                { variant: "error" }
            );
            return;
        }
        uploadFile(file);
    };

    const handleInputChange = (e) => {
        handleFileSelect(e.target.files);
        e.target.value = "";
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFileSelect(e.dataTransfer.files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    // Delete document
    const handleDeleteConfirm = async () => {
        if (!docToDelete) return;
        try {
            setDeleting(true);
            await insightsApi.deleteDocument(docToDelete.id);
            setDocuments((prev) => prev.filter((d) => d.id !== docToDelete.id));
            setDocumentsTotalCount((prev) => Math.max(0, prev - 1));
            enqueueSnackbar(
                intl.formatMessage({ id: "documents.delete_success", defaultMessage: "Document deleted successfully" }),
                { variant: "success", autoHideDuration: 2000, anchorOrigin: { vertical: "bottom", horizontal: "right" } }
            );
            setDeleteDialogOpen(false);
            setDocToDelete(null);
        } catch (error) {
            console.error("Delete failed:", error);
            enqueueSnackbar(
                roleRefusalMessage(error, intl) ||
                intl.formatMessage({ id: "documents.delete_failed", defaultMessage: "Failed to delete document" }),
                { variant: "error" }
            );
        } finally {
            setDeleting(false);
        }
    };

    // Where an upload lands: the fixed scope, or whatever the picker has.
    const uploadProjectId = scoped ? projectId : selectedProjectId;
    const uploadDisabled = uploading || !uploadProjectId;

    // Unscoped, only the projects the caller may change are upload targets,
    // and a row's delete follows its own project's role. A project missing
    // from the loaded cards is left to the BE to decide.
    const roleByProject = Object.fromEntries(projects.map((p) => [String(p.id), p.my_role]));
    const uploadTargets = projects.filter((p) => canEditProjectContent(p.my_role));
    const canUpload = scoped ? canEdit : projectsLoading || uploadTargets.length > 0;
    const canDeleteDoc = (doc) =>
        scoped
            ? canEdit
            : canEditProjectContent(doc.project ? roleByProject[String(doc.project.id)] : undefined);

    return (
        <Box sx={scoped ? {} : { p: 3, maxWidth: 900, mx: "auto" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                {scoped ? (
                    <Box />
                ) : (
                    <Typography variant="h5" sx={{ fontWeight: "bold", color: "#00438E" }}>
                        <FormattedMessage id="documents.title" defaultMessage="Documents" />
                    </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {!scoped && canUpload && (
                    <ProjectPicker
                        id="documents-project"
                        projects={uploadTargets}
                        value={selectedProjectId}
                        // The upload API takes the numeric id, as it always has.
                        onChange={(id) => setSelectedProjectId(id === "" ? "" : Number(id))}
                        loading={projectsLoading}
                        disabled={uploading}
                        label={intl.formatMessage({ id: "documents.project", defaultMessage: "Project" })}
                    />
                    )}
                    <IconButton onClick={() => fetchDocuments(documentsPage)} disabled={loading}>
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Upload Area — absent for a viewer, who has nowhere to upload to. */}
            {canUpload && (
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    mb: 4,
                    borderRadius: 2,
                    borderLeft: "4px solid #00438E",
                    backgroundColor: "#ffffff",
                }}
            >
                <Typography variant="h6" gutterBottom sx={{ color: "#00438E", fontWeight: "bold" }}>
                    <FormattedMessage id="documents.upload_documents" defaultMessage="Upload Documents" />
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    <FormattedMessage
                        id="documents.upload_description"
                        defaultMessage="Upload files from your local device. Supported formats: PDF, DOC, DOCX, PPTX, XLS, XLSX."
                    />
                </Typography>

                {/* Drag & Drop Zone */}
                <Box
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => !uploadDisabled && fileInputRef.current?.click()}
                    sx={{
                        border: dragOver ? "2px solid #00438E" : "2px dashed #bdbdbd",
                        borderRadius: 2,
                        p: 5,
                        textAlign: "center",
                        cursor: uploadDisabled ? "default" : "pointer",
                        bgcolor: dragOver ? "rgba(0, 67, 142, 0.04)" : "#fafafa",
                        transition: "all 0.2s ease",
                        opacity: uploadDisabled ? 0.6 : 1,
                        "&:hover": {
                            borderColor: uploadDisabled ? undefined : "#00438E",
                            bgcolor: uploadDisabled ? undefined : "rgba(0, 67, 142, 0.04)",
                        },
                    }}
                >
                    {uploading ? (
                        <CircularProgress size={40} sx={{ color: "#00438E", mb: 1 }} />
                    ) : (
                        <CloudUploadIcon sx={{ fontSize: 48, color: "#00438E", mb: 1 }} />
                    )}
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                        {uploading ? (
                            <FormattedMessage id="documents.uploading" defaultMessage="Uploading..." />
                        ) : (
                            <FormattedMessage id="documents.drag_drop" defaultMessage="Drag & drop files here" />
                        )}
                    </Typography>
                    {!uploading && (
                        <Typography variant="body2" color="text.secondary">
                            {uploadProjectId ? (
                                <FormattedMessage id="documents.or_browse" defaultMessage="or click to browse from your device" />
                            ) : (
                                <FormattedMessage
                                    id="documents.select_project_first"
                                    defaultMessage="Select a project before uploading"
                                />
                            )}
                        </Typography>
                    )}
                </Box>

                <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx,.pptx,.xls,.xlsx"
                    onChange={handleInputChange}
                    data-testid="documents-file-input"
                />

                <Button
                    variant="contained"
                    startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadDisabled}
                    sx={{
                        mt: 2,
                        backgroundColor: "#00438E",
                        "&:hover": { backgroundColor: "#003A75" },
                    }}
                >
                    <FormattedMessage id="documents.browse_files" defaultMessage="Browse Files" />
                </Button>
            </Paper>
            )}

            {/* Documents List */}
            <Paper
                elevation={3}
                sx={{
                    p: 3,
                    borderRadius: 2,
                    borderLeft: "4px solid #00438E",
                    backgroundColor: "#ffffff",
                }}
            >
                <Typography variant="h6" gutterBottom sx={{ color: "#00438E", fontWeight: "bold" }}>
                    <FormattedMessage
                        id="documents.uploaded_files"
                        defaultMessage="Uploaded Files ({count})"
                        values={{ count: documentsTotalCount }}
                    />
                </Typography>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : documents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                        <FormattedMessage id="documents.no_documents" defaultMessage="No documents uploaded yet." />
                    </Typography>
                ) : (
                    <>
                    <List>
                        {documents.map((doc) => (
                            <ListItem
                                key={doc.id}
                                sx={{
                                    borderRadius: 1,
                                    mb: 1,
                                    border: "1px solid #e0e0e0",
                                    bgcolor: "#fafafa",
                                }}
                            >
                                <ListItemIcon>
                                    <DescriptionIcon sx={{ color: "#00438E" }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={doc.name}
                                    secondary={
                                        <>
                                            {formatDate(doc.created_at)}
                                            {/* A document belongs to one project. `project` is null for
                                                legacy documents uploaded before scoping existed. Inside a
                                                project's own tab every row shares one project, so the chip
                                                would repeat the same value down the list. */}
                                            {!scoped && (
                                            <Box component="span" sx={{ display: "block", mt: 0.5 }}>
                                                <Chip
                                                    icon={<FolderIcon sx={{ fontSize: "0.9rem" }} />}
                                                    label={
                                                        doc.project?.name ||
                                                        (doc.project
                                                            ? `#${doc.project.id}`
                                                            : intl.formatMessage({
                                                                  id: "documents.no_project",
                                                                  defaultMessage: "No project",
                                                              }))
                                                    }
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        fontSize: "0.7rem",
                                                        ...(doc.project ? {} : { color: "text.disabled" }),
                                                    }}
                                                />
                                            </Box>
                                            )}
                                        </>
                                    }
                                />
                                {doc.summary && (
                                    <Chip
                                        label="Summary"
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        clickable
                                        onClick={() => {
                                            setSummaryDoc(doc);
                                            setSummaryModalOpen(true);
                                        }}
                                        sx={{ mr: 1, cursor: "pointer" }}
                                    />
                                )}
                                <IconButton
                                    onClick={() => handleDownloadDocument(doc)}
                                    disabled={downloadingId === doc.id}
                                    sx={{ color: "#00438E", mr: 0.5 }}
                                    title={intl.formatMessage({
                                        id: "documents.download",
                                        defaultMessage: "Download document",
                                    })}
                                >
                                    {downloadingId === doc.id ? (
                                        <CircularProgress size={18} sx={{ color: "#00438E" }} />
                                    ) : (
                                        <DownloadIcon fontSize="small" />
                                    )}
                                </IconButton>
                                {canDeleteDoc(doc) && (
                                <IconButton
                                    onClick={() => {
                                        setDocToDelete(doc);
                                        setDeleteDialogOpen(true);
                                    }}
                                    sx={{ color: "error.main" }}
                                    title={intl.formatMessage({
                                        id: "documents.delete_document",
                                        defaultMessage: "Delete document",
                                    })}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                                )}
                            </ListItem>
                        ))}
                    </List>

                    {!loading && documentsTotalPages > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 3, pb: 1 }}>
                            <Pagination
                                count={documentsTotalPages}
                                page={documentsPage}
                                onChange={(_, page) => fetchDocuments(page)}
                                color="primary"
                                sx={{
                                    "& .MuiPaginationItem-root": { fontSize: "0.875rem" },
                                }}
                            />
                        </Box>
                    )}
                    </>
                )}
            </Paper>

            {/* ─── Delete Confirmation Dialog ─── */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => !deleting && setDeleteDialogOpen(false)}
                PaperProps={{ sx: { borderRadius: "12px", minWidth: "320px" } }}
            >
                <DialogTitle sx={{ fontWeight: "bold", color: "error.main" }}>
                    <FormattedMessage id="documents.delete_dialog_title" defaultMessage="Delete Document" />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <FormattedMessage
                            id="documents.delete_dialog_body"
                            defaultMessage='Are you sure you want to delete "{name}"? This action cannot be undone.'
                            values={{ name: <strong>{docToDelete?.name}</strong> }}
                        />
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={deleting}
                        variant="outlined"
                        size="small"
                    >
                        <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        disabled={deleting}
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                    >
                        <FormattedMessage id="documents.delete_confirm" defaultMessage="Delete" />
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ─── Summary Modal ─── */}
            <Dialog
                open={summaryModalOpen}
                onClose={() => {
                    setSummaryModalOpen(false);
                    setSummaryDoc(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#00438E" }}>
                        <FormattedMessage id="documents.summary_title" defaultMessage="Document Summary" />
                    </Typography>
                    {summaryDoc && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {summaryDoc.name}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                        {summaryDoc?.summary}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => {
                            setSummaryModalOpen(false);
                            setSummaryDoc(null);
                        }}
                        variant="contained"
                        sx={{
                            backgroundColor: "#00438E",
                            "&:hover": { backgroundColor: "#003A75" },
                        }}
                    >
                        <FormattedMessage id="general.close" defaultMessage="Close" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
